import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Fade,
  Autocomplete,
  Chip,
} from '@mui/material';
import { UserCheck, UserPlus, Mail } from 'lucide-react';
import { Board, WorkspaceMemberRole } from '../../types/common';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { invitationsApi } from '../../api/endpoints/invitationsApi';
import { workspacesApi } from '../../api/endpoints/workspacesApi';
import { usersApi } from '../../api/endpoints/usersApi';
import { showToast } from '../../store/uiSlice';

interface AddMemberModalProps {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
}

interface ExistingUserOption {
  id: number;
  fullName: string;
  userName: string;
  email: string;
}

const NO_BOARD = '__workspace_only__';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AddMemberModal: React.FC<AddMemberModalProps> = ({ open, onClose, workspaceId }) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [boards, setBoards] = useState<Board[]>([]);
  const [boardsLoading, setBoardsLoading] = useState(false);

  // Search-as-you-type against the platform's registered users - lets an
  // admin add someone who already has an account directly, without a round
  // trip through email invitation. Separate from "email" below: this is
  // free text used purely to query /users/search.
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState<ExistingUserOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ExistingUserOption | null>(null);

  const [formData, setFormData] = useState({
    email: '',
    role: WorkspaceMemberRole.MEMBER,
    boardId: NO_BOARD,
  });

  const [errors, setErrors] = useState({
    email: '',
  });

  // "Already a member" gets its own fancy popup instead of the usual
  // corner toast - it's a common, expected outcome (not a real failure)
  // and deserves more visibility than a toast the user might miss.
  const [alreadyMemberOpen, setAlreadyMemberOpen] = useState(false);
  const [alreadyMemberEmail, setAlreadyMemberEmail] = useState('');

  // Load the workspace's boards so the admin can optionally scope the
  // invite to one of them (in addition to the workspace itself).
  useEffect(() => {
    if (!open || !workspaceId) return;
    setBoardsLoading(true);
    workspacesApi
      .getWorkspaceBoards(workspaceId)
      .then((res) => setBoards(res.boards || []))
      .catch(() => setBoards([]))
      .finally(() => setBoardsLoading(false));
  }, [open, workspaceId]);

  // Debounced search against registered users as the admin types.
  useEffect(() => {
    if (!open || searchInput.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(() => {
      usersApi
        .searchUsers(searchInput.trim())
        .then((results) => {
          if (!cancelled) setSearchResults(results);
        })
        .catch(() => {
          if (!cancelled) setSearchResults([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, searchInput]);

  const handleInputChange = (field: string, value: string | WorkspaceMemberRole) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'email' && errors.email) {
      setErrors((prev) => ({ ...prev, email: '' }));
    }
  };

  const validateEmail = (email: string) => EMAIL_PATTERN.test(email);

  const validateForm = () => {
    const newErrors = { email: '' };

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return !newErrors.email;
  };

  // "Add existing user" - direct membership, no email round trip. Only
  // scopes to the workspace (the direct-add endpoint doesn't take a board
  // yet); use "Invite" below if you also need to land them on a board.
  const handleAddExisting = async () => {
    if (!selectedUser) return;

    setLoading(true);
    try {
      await workspacesApi.addMember(workspaceId, {
        email: selectedUser.email,
        role: formData.role,
      });
      dispatch(
        showToast({
          message: `${selectedUser.fullName || selectedUser.email} added to the workspace`,
          type: 'success',
        }),
      );
      handleClose();
    } catch (error: any) {
      const status = error?.response?.status;
      const serverMessage: string = error?.response?.data?.message || '';
      if (status === 409 || /already a member/i.test(serverMessage)) {
        setAlreadyMemberEmail(selectedUser.email);
        setAlreadyMemberOpen(true);
      } else {
        dispatch(
          showToast({
            message: serverMessage || error?.message || 'Failed to add member',
            type: 'error',
          }),
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await invitationsApi.createInvitation(workspaceId, {
        email: formData.email.trim(),
        role: formData.role,
        boardId: formData.boardId !== NO_BOARD ? formData.boardId : undefined,
      });

      const board = boards.find((b) => b.id === formData.boardId);
      dispatch(
        showToast({
          message: board
            ? `Invitation sent to ${formData.email.trim()} for "${board.name}"`
            : `Invitation sent to ${formData.email.trim()}`,
          type: 'success',
        })
      );

      handleClose();
    } catch (error: any) {
      const status = error?.response?.status;
      const serverMessage: string = error?.response?.data?.message || '';

      if (status === 409 && /already a member/i.test(serverMessage)) {
        setAlreadyMemberEmail(formData.email.trim());
        setAlreadyMemberOpen(true);
      } else {
        dispatch(
          showToast({
            message: serverMessage || error?.message || 'Failed to send invitation',
            type: 'error',
          })
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      email: '',
      role: WorkspaceMemberRole.MEMBER,
      boardId: NO_BOARD,
    });
    setErrors({ email: '' });
    setSearchInput('');
    setSearchResults([]);
    setSelectedUser(null);
    onClose();
  };

  return (
    <>
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Member to Workspace</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <UserCheck size={16} /> Add an existing user
          </Typography>
          <Autocomplete
            options={searchResults}
            value={selectedUser}
            onChange={(_, value) => setSelectedUser(value)}
            inputValue={searchInput}
            onInputChange={(_, value) => {
              setSearchInput(value);
              setSelectedUser(null);
            }}
            getOptionLabel={(u) => u.fullName || u.email}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            loading={searching}
            noOptionsText={
              searchInput.trim().length < 2
                ? 'Type at least 2 characters to search'
                : 'No matching users - use "Invite by email" below instead'
            }
            renderOption={(props, u) => (
              <li {...props} key={u.id}>
                <Box>
                  <Typography variant="body2">{u.fullName || u.userName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {u.email}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search by name, username, or email..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searching && <CircularProgress size={16} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {selectedUser && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Chip
                icon={<UserPlus size={14} />}
                label={`${selectedUser.fullName || selectedUser.email} will be added directly`}
                color="primary"
                variant="outlined"
                size="small"
              />
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 2.5 }}>
            <Box sx={{ flex: 1, borderTop: '1px solid', borderColor: 'divider' }} />
            <Typography variant="caption" color="text.secondary">
              OR INVITE SOMEONE NEW
            </Typography>
            <Box sx={{ flex: 1, borderTop: '1px solid', borderColor: 'divider' }} />
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Mail size={16} /> Invite by email
          </Typography>
          <TextField
            fullWidth
            label="Gmail / Email Address"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            error={!!errors.email}
            helperText={errors.email || "We'll email them a link to join this workspace"}
            placeholder="user@example.com"
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={formData.role}
              onChange={(e) => handleInputChange('role', e.target.value as WorkspaceMemberRole)}
              label="Role"
            >
              <MenuItem value={WorkspaceMemberRole.VIEWER}>
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    Viewer
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can view workspace and boards
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value={WorkspaceMemberRole.MEMBER}>
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    Member
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can create and edit boards and tasks
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value={WorkspaceMemberRole.ADMIN}>
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    Admin
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can manage workspace settings and members
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Board (optional)</InputLabel>
            <Select
              value={formData.boardId}
              onChange={(e) => handleInputChange('boardId', e.target.value)}
              label="Board (optional)"
              disabled={boardsLoading}
            >
              <MenuItem value={NO_BOARD}>
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    Whole workspace
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    They'll join the workspace but no specific board
                  </Typography>
                </Box>
              </MenuItem>
              {boards.map((board) => (
                <MenuItem key={board.id} value={board.id}>
                  {board.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {formData.boardId === NO_BOARD && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Note: "Add an existing user" above only grants workspace access. Pick a board here
              and use "Send Invitation" if they also need direct access to a specific project.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        {selectedUser ? (
          <Button variant="contained" onClick={handleAddExisting} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Add Member'}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !formData.email.trim()}
          >
            {loading ? <CircularProgress size={24} /> : 'Send Invitation'}
          </Button>
        )}
      </DialogActions>
    </Dialog>

    {/* Fancy standalone popup for the one specific, common, non-error
        outcome: the invitee is already in this workspace. */}
    <Dialog
      open={alreadyMemberOpen}
      onClose={() => setAlreadyMemberOpen(false)}
      maxWidth="xs"
      fullWidth
      TransitionComponent={Fade}
      PaperProps={{
        sx: {
          borderRadius: 4,
          textAlign: 'center',
          p: 1,
        },
      }}
    >
      <DialogContent sx={{ pt: 4, pb: 2 }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: 'primary.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
          }}
        >
          <UserCheck size={32} color="#fff" />
        </Box>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
          Already on the team!
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>{alreadyMemberEmail}</strong> is already a member of this workspace, so there's
          nothing to invite.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button variant="contained" onClick={() => setAlreadyMemberOpen(false)} sx={{ px: 4, borderRadius: 2 }}>
          Got it
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default AddMemberModal;
